import {
  $CombinedState,
  createAsyncThunk,
  createSelector,
  createSlice,
  isAnyOf,
} from "@reduxjs/toolkit";

import * as api from "../../api";
import { ApiError } from "../../api/error";
import { getConfig } from "../../api/getConfig";
import { FormType } from "../../components/enums";
import { RootState } from "../../store/rootReducer";
import {
  selectACFormData,
  selectOCVehicleFormData,
} from "../../store/rootSelectors";

enum RequestStatus {
  IDLE = "idle",
  PENDING = "pending",
  SUCCESS = "success",
  ERROR = "error",
}

type SenderState = {
  caseId: string;
  msg: string;
  status: RequestStatus;
  isSent: boolean;
};

const initialState: SenderState = {
  caseId: "",
  msg: "",
  status: RequestStatus.IDLE,
  isSent: false,
};

export const postTextData = createAsyncThunk<
  string,
  {
    data:
      | ReturnType<typeof selectACFormData>
      | ReturnType<typeof selectOCVehicleFormData>;
    formType: FormType;
  },
  { state: RootState; rejectValue: string }
>(
  "sender/sendCase",
  async ({ data, formType }, { getState, rejectWithValue }) => {
    //const endpoint = getState().appData.config.apiEndpoint;
    //GIELK - Endpoint domyślnie brany z pliku appData.json co się odbywa na WelcomePage - w przypadku korzystania z linków bezpośrednich OC/AC nie ma możliwości pobrania endpointów z Welcome które jest pomijane.
    let endpoint = getState().appData.config.apiEndpoint;
    if (!endpoint) {
      const UATHOST = "my.uat.ayvens.pl";
      const DEVHOST = "portal.dev.leaseplan.pl";
      const PRODHOST = "my.ayvens.pl";
      const CURRENTHOST = window.location.hostname;
      if (CURRENTHOST === UATHOST) {
        endpoint = "https://my.uat.ayvens.pl/Likwidacja/api/FNOL";
        console.log(
          "INFO - EMPTY ENDPOINT FOR SENDING DATA DETECTED - REWRITING ENDPOINT TO UAT DEFAULT"
        );
      } else if (CURRENTHOST === DEVHOST) {
        endpoint = "https://portal.dev.leaseplan.pl/Likwidacja/api/FNOL";
        console.log(
          "INFO - EMPTY ENDPOINT FOR SENDING DATA DETECTED - REWRITING ENDPOINT TO DEV ( UAT ) DEFAULT"
        );
      } else if (CURRENTHOST === PRODHOST) {
        endpoint = "https://my.ayvens.pl/Likwidacja/api/FNOL";
        console.log(
          "INFO - EMPTY ENDPOINT FOR SENDING DATA DETECTED - REWRITING ENDPOINT TO PROD DEFAULT"
        );
      } else {
        console.log(
          "ERROR - WRONG HOSTNAME DETECTED - SENDING DATA TO EMPTY ENDPOINT"
        );
      }
    }
    try {
      const status = await api.postTextData(endpoint, data, formType);

      return status;
    } catch (error: unknown) {
      return rejectWithValue((error as ApiError).getObject().message as string);
    }
  }
);

export const postImages = createAsyncThunk<
  boolean | string,
  { type: string; images: Array<string> },
  { state: RootState; rejectValue: string }
>(
  "sender/uploadImages",
  async ({ type, images }, { getState, rejectWithValue }) => {
    //const endpoint = getState().appData.config.apiEndpoint;
    //GIELK - Endpoint domyślnie brany z pliku appData.json co się odbywa na WelcomePage - w przypadku korzystania z linków bezpośrednich OC/AC nie ma możliwości pobrania endpointów z Welcome które jest pomijane.
    let endpoint = getState().appData.config.apiEndpoint;
    if (!endpoint) {
      const UATHOST = "my.uat.ayvens.pl";
      const DEVHOST = "portal.dev.leaseplan.pl";
      const PRODHOST = "my.ayvens.pl";
      const CURRENTHOST = window.location.hostname;
      if (CURRENTHOST === UATHOST) {
        endpoint = "https://my.uat.ayvens.pl/Likwidacja/api/FNOL";
        console.log(
          "INFO - EMPTY ENDPOINT FOR SENDING DATA DETECTED - REWRITING ENDPOINT TO UAT DEFAULT"
        );
      } else if (CURRENTHOST === DEVHOST) {
        endpoint = "https://portal.dev.leaseplan.pl/Likwidacja/api/FNOL";
        console.log(
          "INFO - EMPTY ENDPOINT FOR SENDING DATA DETECTED - REWRITING ENDPOINT TO DEV ( UAT ) DEFAULT"
        );
      } else if (CURRENTHOST === PRODHOST) {
        endpoint = "https://my.ayvens.pl/Likwidacja/api/FNOL";
        console.log(
          "INFO - EMPTY ENDPOINT FOR SENDING DATA DETECTED - REWRITING ENDPOINT TO PROD DEFAULT"
        );
      } else {
        console.log(
          "ERROR - WRONG HOSTNAME DETECTED - SENDING DATA TO EMPTY ENDPOINT"
        );
      }
    }
    try {
      const caseId = getState().sender.caseId;
      const status = await api.postImages(endpoint, caseId, type, images);

      return status;
    } catch (error: unknown) {
      return rejectWithValue((error as ApiError).getObject().message as string);
    }
  }
);

export const generatePDF = createAsyncThunk<
  boolean,
  { formType: FormType },
  { state: RootState; rejectValue: string }
>("sender/generatePDF", async ({ formType }, { getState, rejectWithValue }) => {
  //const endpoint = getState().appData.config.apiPDFEndpoint;
  //GIELK - Endpoint domyślnie brany z pliku appData.json co się odbywa na WelcomePage - w przypadku korzystania z linków bezpośrednich OC/AC nie ma możliwości pobrania endpointów z Welcome które jest pomijane.
  let endpoint = getState().appData.config.apiPDFEndpoint;
  if (!endpoint) {
    const UATHOST = "my.uat.ayvens.pl";
    const DEVHOST = "portal.dev.leaseplan.pl";
    const PRODHOST = "my.ayvens.pl";
    const CURRENTHOST = window.location.hostname;
    if (CURRENTHOST === UATHOST) {
      endpoint = "https://my.uat.ayvens.pl/Likwidacja/FNOL";
      console.log(
        "INFO - EMPTY ENDPOINT FOR PDF CREATION DETECTED - REWRITING ENDPOINT TO UAT DEFAULT"
      );
    } else if (CURRENTHOST === DEVHOST) {
      endpoint = "https://portal.dev.leaseplan.pl/Likwidacja/FNOL";
      console.log(
        "INFO - EMPTY ENDPOINT FOR PDF CREATION DETECTED - REWRITING ENDPOINT TO DEV ( UAT ) DEFAULT"
      );
    } else if (CURRENTHOST === PRODHOST) {
      endpoint = "https://my.ayvens.pl/Likwidacja/FNOL";
      console.log(
        "INFO - EMPTY ENDPOINT FOR PDF CREATION DETECTED - REWRITING ENDPOINT TO PROD DEFAULT"
      );
    } else {
      console.log(
        "ERROR - WRONG HOSTNAME DETECTED - SENDING DATA TO EMPTY ENDPOINT"
      );
    }
  }
  try {
    const caseId = getState().sender.caseId;
    await api.generatePDF(endpoint, caseId, formType);

    return true;
  } catch (error: unknown) {
    return rejectWithValue((error as ApiError).getObject().message as string);
  }
});

const senderSlice = createSlice({
  name: "sender",
  initialState,
  reducers: {
    resetSender: () => initialState,
    fulfillSender: (state) => {
      state.status = RequestStatus.SUCCESS;
      state.isSent = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(postTextData.pending, (state) => {
      state.status = RequestStatus.PENDING;
      state.msg = "Trwa wysyłanie formularza";
    });
    builder.addCase(postTextData.fulfilled, (state, { payload }) => {
      state.caseId = payload;
    });
    builder.addCase(postImages.pending, (state) => {
      state.msg = "Trwa wysyłanie zdjęć";
    });
    builder.addMatcher(
      isAnyOf(postTextData.rejected, postImages.rejected, generatePDF.rejected),
      (state, { payload }) => {
        state.status = RequestStatus.ERROR;

        if (payload) {
          state.msg = payload;
        }
      }
    );
  },
});

const statusSelector = (state: RootState) => state.sender.status;
const sentFlagSelector = (state: RootState) => state.sender.isSent;
export const isCaseSentSuccessfully = createSelector(
  statusSelector,
  sentFlagSelector,
  (status, sentFlag) => status === RequestStatus.SUCCESS && sentFlag
);
export const doesErrorOccured = createSelector(
  statusSelector,
  (status) => status === RequestStatus.ERROR
);

export const { fulfillSender, resetSender } = senderSlice.actions;

export const { reducer } = senderSlice;
